import * as Sentry from "@sentry/react";

const CREATED_ORDER_IDS_KEY = "createdOrderIds";

/**
 * Get all created order IDs from sessionStorage
 */
export function getCreatedOrderIds(): number[] {
  try {
    const stored = sessionStorage.getItem(CREATED_ORDER_IDS_KEY);
    if (!stored) {
      return [];
    }
    const parsed = JSON.parse(stored);
    const ids = Array.isArray(parsed) ? parsed : [];
    // Ensure all IDs are numbers (JSON.parse should preserve numbers, but double-check)
    const numericIds = ids
      .map((id) => (typeof id === "string" ? Number(id) : id))
      .filter((id): id is number => typeof id === "number" && !isNaN(id));
    return numericIds;
  } catch (error) {
    console.error(
      "Error reading created order IDs from sessionStorage:",
      error
    );
    Sentry.captureException(error);
    return [];
  }
}

/**
 * Add an order ID to the created order IDs array in sessionStorage
 */
export function addCreatedOrderId(orderId: number): void {
  try {
    const currentIds = new Set(getCreatedOrderIds());
    if (!currentIds.has(orderId)) {
      currentIds.add(orderId);
      sessionStorage.setItem(
        CREATED_ORDER_IDS_KEY,
        JSON.stringify(Array.from(currentIds))
      );
    }
  } catch (error) {
    console.error("Error adding created order ID to sessionStorage:", error);
    Sentry.captureException(error);
  }
}

/**
 * Remove an order ID from the created order IDs array in sessionStorage
 */
export function removeCreatedOrderId(orderId: number): void {
  try {
    const currentIds = new Set(getCreatedOrderIds());
    currentIds.delete(orderId);
    sessionStorage.setItem(
      CREATED_ORDER_IDS_KEY,
      JSON.stringify(Array.from(currentIds))
    );
  } catch (error) {
    console.error(
      "Error removing created order ID from sessionStorage:",
      error
    );
    Sentry.captureException(error);
  }
}

/**
 * Check if an order ID exists in the created order IDs array
 */
export function isCreatedOrderId(orderId: number): boolean {
  const currentIds = getCreatedOrderIds();
  return currentIds.some((id) => id === orderId);
}

/**
 * Clear all created order IDs from sessionStorage
 */
export function clearCreatedOrderIds(): void {
  try {
    sessionStorage.removeItem(CREATED_ORDER_IDS_KEY);
  } catch (error) {
    console.error(
      "Error clearing created order IDs from sessionStorage:",
      error
    );
    Sentry.captureException(error);
  }
}
